// proformaMobileApi.js — génération du PDF d'un devis.
//
// Le desktop enferme cette logique dans `handleDownloadInvoice` (GestionProforma.tsx:2453),
// une fonction du composant : impossible à importer. On rejoue ici la même chaîne, en
// réutilisant l'objet `api` du desktop et le template `generateProformaHTML`.
//
// La branche de repli du desktop (l.2641) repostait vers le MÊME endpoint : on ne la
// reproduit pas, elle ne pouvait rien rattraper.
import API_CONFIG from "../../config/api";
import { api } from "../../gestion-proforma/GestionProforma";
import { generateProformaHTML } from "../../gestion-proforma/ProformaHTMLTemplate";

const BASE_URL = API_CONFIG.BASE_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const fmtDay = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR");
};

/** Télécharge le PDF d'un devis. `clients` sert à retrouver l'objet client du template. */
export async function downloadProformaPdf(record, clients = []) {
  const id = String(record.id);

  const detail = await api.fetchProformaDetail(id);
  let produitsVendus = detail.produitsVendus || [];
  if (!produitsVendus.length) {
    produitsVendus = await api.fetchOrderItems(id);
  }

  // Enrichissement des unités / noms produits (l.2472-2521) — non bloquant.
  try {
    const productIds = produitsVendus.map((p) => p.produit_id || p.product_id).filter(Boolean);
    if (productIds.length) {
      const details = await api.fetchProductDetails(productIds);
      produitsVendus = produitsVendus.map((p) => {
        const productId = p.produit_id || p.product_id;
        const d = details.find((x) => x.id === productId);
        return d
          ? {
              ...p,
              unit_short_name: d.unit_short_name || "",
              unit_name: d.unit_name || "",
              product_name: d.name || p.nom_produit || p.product_name || `Produit #${productId}`,
            }
          : { ...p, product_name: p.nom_produit || p.product_name || `Produit #${productId}` };
      });
    }
  } catch {
    /* on continue avec les données non enrichies, comme le desktop */
  }

  detail.produitsVendus = produitsVendus;
  const warehouse = await api.fetchWarehouse(record.warehouse_id);
  const client = clients.find((c) => c.id === detail.user_id);
  const invoiceData = { ...detail, produitsVendus, warehouse, client };

  const htmlContent = generateProformaHTML({
    order: invoiceData,
    clientObj: client,
    warehouse,
    totals: {
      subtotal: invoiceData.subtotal,
      totalDiscount: invoiceData.discount || 0,
      totalTax: invoiceData.tax_amount || 0,
      total: invoiceData.total,
    },
    formattedDate: fmtDay(invoiceData.order_date),
    formatNumber,
  });

  const fileName = `Proforma_${invoiceData.invoice_number}.pdf`;
  const response = await fetch(`${BASE_URL}/api/orders/generate-proforma-pdf-react`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ orderData: { ...invoiceData, htmlContent }, fileName }),
  });

  if (!response.ok) {
    let msg = "Erreur serveur lors de la génération du PDF";
    try {
      const err = await response.json();
      msg = err.error || msg;
    } catch {
      /* réponse non JSON */
    }
    throw new Error(msg);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}
